import { useStore } from '@nanostores/react'
import { Dialog } from 'radix-ui'
import { atom } from 'nanostores'
import { type ReactNode, useTransition } from 'react'
import { cn } from '../../cn'
import {
  GrantPermissionDialog,
  type GrantPermissionDialogMessages,
  type GrantPermissionDialogProps,
} from './grant-permission-dialog'
import { BackArrowIcon, BellIcon, StarIcon, UnlockIcon } from './icons'

export {
  grantPermissionDialog,
  type GrantPermissionDialogMessages,
} from './grant-permission-dialog'

const $isOpen = atom(false)

export function openTimelineView() {
  $isOpen.set(true)
}

export interface TrialTimelineMessages extends GrantPermissionDialogMessages {
  subscription_walkthrough_p3_title: () => string
  btn_try_plus_free: () => string
  onboarding_paywall_footer: (params: {
    trial_day_number: number
    plan_price: string
  }) => string
  subscription_walkthrough_p3_today: () => string
  subscription_walkthrough_p3_today_note: () => string
  subscription_walkthrough_p3_1_day_before_trail: () => string
  subscription_walkthrough_p3_1_day_before_trail_note: () => string
  subscription_walkthrough_p3_trail_end: () => string
  subscription_walkthrough_p3_trail_end_note: () => string
}

export interface TrialTimelineViewProps {
  requestNotificationPermission: () => Promise<void>
  startTrial: () => Promise<void>
  backgroundLayer: ReactNode
  m: TrialTimelineMessages
  eligibleTrialDays: number
  planPrice: string
  titleColorStyle?: string
  className?: string
  /** Controlled open state */
  open?: boolean
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void
  /** Props for the grant permission dialog (used in controlled mode) */
  grantPermissionDialogProps?: Pick<
    GrantPermissionDialogProps,
    'open' | 'onOpenChange' | 'expiresAt'
  >
}

export function TrialTimelineView(props: TrialTimelineViewProps) {
  const internalIsOpen = useStore($isOpen)
  const [isPending, startTransition] = useTransition()
  const titleColorStyle = props.titleColorStyle ?? 'color: #2AFD00;'

  // Use controlled state if provided, otherwise use internal state
  const isControlled = props.open !== undefined
  const isOpen = isControlled ? props.open : internalIsOpen

  const handleClose = () => {
    if (isControlled) {
      props.onOpenChange?.(false)
    } else {
      $isOpen.set(false)
    }
  }

  const handleClick = () => {
    startTransition(async () => {
      try {
        await props.startTrial()
      } catch {
        //
      }
    })
  }

  return (
    <>
      <Dialog.Root open={isOpen}>
        <Dialog.Portal>
          <Dialog.Content
            className={cn(
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 fixed inset-0 z-50 bg-[#001721]',
              props.className,
            )}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            aria-describedby={undefined}
          >
            {/* Background */}
            <div className="pointer-events-none absolute inset-0">
              {props.backgroundLayer}
            </div>

            <div className="relative mx-auto max-w-[640px] space-y-12 px-8 pt-[120px]">
              <Dialog.Title
                className="px-4 text-center text-2xl font-bold whitespace-pre-line text-white"
                dangerouslySetInnerHTML={{
                  __html: props.m
                    .subscription_walkthrough_p3_title()
                    .replace('[%2$s]', titleColorStyle),
                }}
              />

              {/* Timeline Section (inlined) */}
              <div className="relative flex flex-col gap-8 px-6 text-white">
                {/* Decorative bar */}
                <div
                  className="pointer-events-none absolute top-4 left-[41px] h-[319px] w-2"
                  style={{
                    backgroundImage:
                      'linear-gradient(180deg, #81D200 -14.68%, #0EBC73 26.07%, rgba(8, 193, 243, 0.66) 59.8%, rgba(8, 193, 243, 0.00) 82.71%)',
                  }}
                />

                <div className="relative flex gap-[10px] pl-[6px]">
                  <div className="size-[30px] shrink-0">
                    <UnlockIcon />
                  </div>
                  <div className="flex-1 space-y-1">
                    <h2 className="font-bold">
                      {props.m.subscription_walkthrough_p3_today()}
                    </h2>
                    <p className="text-sm">
                      {props.m.subscription_walkthrough_p3_today_note()}
                    </p>
                  </div>
                </div>
                <div className="relative flex gap-[10px] pl-[6px]">
                  <div className="size-[30px] shrink-0">
                    <BellIcon />
                  </div>
                  <div className="flex-1 space-y-1">
                    <h2 className="font-bold">
                      {props.m.subscription_walkthrough_p3_1_day_before_trail()}
                    </h2>
                    <p className="text-sm">
                      {props.m.subscription_walkthrough_p3_1_day_before_trail_note()}
                    </p>
                  </div>
                </div>
                <div className="relative flex gap-[10px] pl-[6px]">
                  <div className="size-[30px] shrink-0">
                    <StarIcon />
                  </div>
                  <div className="flex-1 space-y-1">
                    <h2 className="font-bold">
                      {props.m.subscription_walkthrough_p3_trail_end()}
                    </h2>
                    <p className="text-sm">
                      {props.m.subscription_walkthrough_p3_trail_end_note()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="fixed right-0 bottom-0 left-0 px-6 pt-3 pb-12">
                <div className="mx-auto max-w-[640px] space-y-3">
                  <button
                    className="h-[52px] w-full max-w-[640px] rounded-xl p-px"
                    style={{
                      backgroundImage:
                        'linear-gradient(180deg, #C5FF70 0%, #69FF78 45%, #30D3FF 100%)',
                    }}
                    onClick={handleClick}
                    disabled={isPending}
                  >
                    <span
                      className="grid h-full w-full place-items-center rounded-[11px] font-bold text-[#176361]"
                      style={{
                        backgroundImage:
                          'linear-gradient(106deg, #C5FF70 13%, #30D3FF 97%)',
                      }}
                    >
                      {props.m.btn_try_plus_free()}
                    </span>
                  </button>
                  <p className="text-center text-xs text-[#FFFFFFE6]">
                    {props.m.onboarding_paywall_footer({
                      trial_day_number: props.eligibleTrialDays,
                      plan_price: props.planPrice,
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Close button */}
            <button
              className="absolute top-[calc(4px+var(--safe-area-top))] left-4 size-8"
              onClick={handleClose}
            >
              <BackArrowIcon />
            </button>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <GrantPermissionDialog
        m={props.m}
        requestNotificationPermission={props.requestNotificationPermission}
        {...props.grantPermissionDialogProps}
      />
    </>
  )
}
